import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key, options) => I18n.t(`erp.sales.js.marking_wizard.${key}`, options)
const STEP_COUNT = 4
const TEXT_INPUT_TYPES = [ "text", "number", "search", "email", "tel", "url" ]
const MILLIMETRES = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 })

// The 4-step wizard of a marking card (_marking_wizard, `#add-mark` of the mockup): technique, placement, logo and
// textiles. It edits the card's own fields, inside the estimate form. A step is done once its required choices are
// made; the steps after the first incomplete one stay blocked. "Ajouter le marquage" closes it and saves the
// estimate; closing a new marking before that discards it (erp--estimate-editor#discardMarking), unless the
// textile chain created it.
// Connects to data-controller="erp--marking-wizard" on the marking card, beside erp--marking-form
export default class extends Controller {
  static targets = [
    "dialog", "title", "step", "stepSummary", "pane", "summary", "previous", "next",
    "dropzone", "fileInput", "pendingFiles", "artworkPending"
  ]

  static values = { new: Boolean }

  connect() {
    this._step = 0
    this._onClose = () => this.#closed()
    this._onEdit = () => this.#refresh()
    this._onKeydown = (event) => this.#keydown(event)
    // Captured on the dialog, so a refused file is set aside before erp--autosave (on the form) sees the input
    this._onFileChange = (event) => { if (event.target === this.fileInputTarget) this.filesChanged() }
    this.dialogTarget.addEventListener("change", this._onFileChange, true)
    this._form = this.element.closest("form")
    this._onSaved = (event) => this.#saved(event)
    this._form?.addEventListener("erp--autosave:request-finished", this._onSaved)
    this.dialogTarget.addEventListener("close", this._onClose)
    this.dialogTarget.addEventListener("input", this._onEdit)
    this.dialogTarget.addEventListener("change", this._onEdit)
    this.dialogTarget.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    this.dialogTarget.removeEventListener("change", this._onFileChange, true)
    this._form?.removeEventListener("erp--autosave:request-finished", this._onSaved)
    this.dialogTarget.removeEventListener("close", this._onClose)
    this.dialogTarget.removeEventListener("input", this._onEdit)
    this.dialogTarget.removeEventListener("change", this._onEdit)
    this.dialogTarget.removeEventListener("keydown", this._onKeydown)
  }

  open(event) {
    event?.preventDefault()
    this._fileError = null
    const title = this.titleTarget.dataset
    this.titleTarget.textContent = this.newValue ? title.newLabel : title.editLabel
    this.#renderPendingFiles()
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    this.#show(0)
  }

  // Reopens on the step holding a field the save found invalid (erp--autosave)
  openOn(field) {
    this.open()
    const pane = field.closest("[data-erp--marking-wizard-target='pane']")
    this.#show(Math.max(0, this.paneTargets.indexOf(pane)))
  }

  go(event) {
    const step = Number(event.params.step)
    if (step <= this.#firstIncomplete()) this.#show(step)
  }

  previous() {
    this.#show(Math.max(0, this._step - 1))
  }

  // A new marking moves on once the step is complete; a saved one may be completed in any order
  next() {
    if (this.newValue && this.#missing(this._step)) return
    if (this._step < STEP_COUNT - 1) return this.#show(this._step + 1)

    this.#finish()
  }

  // ── Logo ─────────────────────────────────────────────────────

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.dataset.over = ""
  }

  dragLeave() {
    delete this.dropzoneTarget.dataset.over
  }

  drop(event) {
    event.preventDefault()
    delete this.dropzoneTarget.dataset.over
    this.#setFiles([ ...this.fileInputTarget.files, ...event.dataTransfer.files ])
  }

  // Runs before erp--autosave, which would empty the whole input over one refused file
  filesChanged() {
    const files = [ ...this.fileInputTarget.files ]
    const accepted = files.filter(file => !this.#fileError(file))
    // An emptied input (the refusal below, or a cleared selection) keeps the last refusal on screen
    if (files.length > 0) this._fileError = files.map(file => this.#fileError(file)).find(Boolean) || null
    if (accepted.length !== files.length) this.fileInputTarget.files = this.#fileList(accepted)
    this.#renderPendingFiles()
    this.#refresh()
  }

  removePendingFile(event) {
    const index = Number(event.params.index)
    this.#setFiles([ ...this.fileInputTarget.files ].filter((_file, position) => position !== index))
  }

  // A document of the estimate becomes a logo of the marking: its blob joins the saved files
  useLibraryFile(event) {
    const { signedId, filename } = event.params
    const container = this.element.querySelector("[data-attachment-hidden-container]")
    if (!container || container.querySelector(`input[value='${CSS.escape(signedId)}']`)) return

    container.querySelectorAll("[data-attachments-cleared]").forEach(input => input.remove())
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.fileInputTarget.name
    hidden.value = signedId
    hidden.dataset.existingAttachment = "true"
    container.appendChild(hidden)
    this.element.querySelector("[data-attachment-list]")?.appendChild(this.#attachmentBadge(signedId, filename))
    hidden.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // ── Steps ────────────────────────────────────────────────────

  #show(step) {
    this._step = step
    this.paneTargets.forEach((pane, index) => { pane.hidden = index !== step })
    this.#refresh()
    this.paneTargets[step]?.closest(".overflow-y-auto")?.scrollTo({ top: 0 })
    this.#focusPane(step)
  }

  #focusPane(step) {
    const pane = this.paneTargets[step]
    const field = pane?.querySelector("input[type='radio']:checked") || pane?.querySelector("input:not([type='hidden']), select, textarea")
    field?.focus({ preventScroll: true })
  }

  #refresh() {
    if (!this.dialogTarget.open) return

    const firstIncomplete = this.#firstIncomplete()
    this.stepTargets.forEach((button, index) => this.#refreshStep(button, index, firstIncomplete))
    this.stepSummaryTargets.forEach((summary, index) => { summary.textContent = this.#stepSummary(index) })
    this.#refreshFooter()
  }

  #refreshStep(button, index, firstIncomplete) {
    button.disabled = index > firstIncomplete
    button.toggleAttribute("data-done", index !== this._step && !this.#missing(index))
    if (index === this._step) button.setAttribute("aria-current", "step")
    else button.removeAttribute("aria-current")
  }

  #refreshFooter() {
    const missing = this.#missing(this._step)
    const last = this._step === STEP_COUNT - 1
    const nextLabel = last ? t(this.newValue ? "add" : "update") : t("next")
    this.nextTarget.querySelector("span").textContent = nextLabel
    this.nextTarget.setAttribute("aria-disabled", String(Boolean(missing) && this.newValue))
    this.previousTarget.hidden = this._step === 0

    if (this._fileError) this.summaryTarget.replaceChildren(this._fileError)
    else if (missing) this.summaryTarget.replaceChildren(t("missing", { what: missing }))
    else if (last) this.summaryTarget.replaceChildren(...this.#recapNodes())
    else this.summaryTarget.replaceChildren(t("step_ok", { step: this._step + 1, total: STEP_COUNT }), " · ", this.#bold(t("step_ok_done")))
  }

  #recapNodes() {
    return [ this.#bold(this.#techniqueLabel()), ` · ${this.#zoneLabel()} · `, this.#bold(t("marked_pieces", { count: this.#markedPieces() })) ]
  }

  #bold(text) {
    const node = document.createElement("b")
    node.textContent = text
    return node
  }

  #firstIncomplete() {
    if (!this.newValue) return STEP_COUNT

    const index = [ 0, 1, 2, 3 ].find(step => this.#missing(step))
    return index ?? STEP_COUNT
  }

  #missing(step) {
    return [ () => this.#techniqueMissing(), () => this.#placementMissing(), () => this.#artworkMissing(), () => this.#textilesMissing() ][step]()
  }

  #stepSummary(step) {
    return [ () => this.#techniqueSummary(), () => this.#placementSummary(), () => this.#artworkSummary(), () => this.#textilesSummary() ][step]()
  }

  // A new marking stays new until the estimate saves it: an invalid field reopens it as it was (erp--autosave)
  #finish() {
    this._awaitingSave = true
    this._closingToSave = true
    this.dialogTarget.close()
    this._form?.requestSubmit()
  }

  #saved(event) {
    if (!this._awaitingSave || !event.detail?.successful) return

    this._awaitingSave = false
    this.newValue = false
  }

  // A marking the textile chain created for the new textiles stays, to complete later
  #closed() {
    if (this._closingToSave) {
      this._closingToSave = false
      return
    }
    if (this.newValue && this.element.dataset.automaticTextileMarking !== "true") this.dispatch("discarded")
  }

  // Enter in a text field moves to the next step instead of submitting the estimate form; it still opens the
  // file picker and checks a box
  #keydown(event) {
    if (event.key !== "Enter" || !TEXT_INPUT_TYPES.includes(event.target.type)) return

    event.preventDefault()
    this.next()
  }

  // ── Step 1 · technique ───────────────────────────────────────

  #technique() {
    return this.element.querySelector("input[type='radio'][name$='[marking_technique_id]']:checked")
  }

  #pricingChoice() {
    return this.paneTargets[0].querySelector("[data-erp--marking-form-target='dimension']")?.value || ""
  }

  // An older marking may carry a technique outside the catalogue: its code still counts
  #techniqueCode() {
    return this.#technique()?.dataset.code || this.element.querySelector("[data-erp--marking-form-target='technique']")?.value || ""
  }

  #techniqueMissing() {
    const code = this.#techniqueCode()
    if (!code) return t("missing_technique")
    if (code === "screen_print" && !this.#pricingChoice()) return t("missing_colours")
    return null
  }

  #techniqueLabel() {
    if (!this.#techniqueCode()) return ""

    return [ this.#techniqueName(), this.#pricingChoiceLabel() ].filter(Boolean).join(" · ")
  }

  #techniqueName() {
    const badge = this.element.querySelector("[data-erp--marking-form-target='techniqueBadge']")
    return this.#technique()?.dataset.label || badge?.textContent?.trim()
  }

  #pricingChoiceLabel() {
    if (!this.#pricingChoice()) return null

    return this.paneTargets[0].querySelector("button[aria-pressed='true']")?.textContent?.trim()
  }

  #techniqueSummary() {
    return this.#techniqueLabel() || t("to_choose")
  }

  // ── Step 2 · placement ───────────────────────────────────────

  // An older marking may only carry the name of its zone (`marking_position`)
  #zoneLabel() {
    const zone = this.element.querySelector("input[type='radio'][name$='[marking_zone_id]']:checked")
    const custom = this.element.querySelector("[data-erp--marking-form-target='customZone']")?.value.trim()
    const position = this.element.querySelector("[data-erp--marking-form-target='position']")?.value.trim()
    return zone?.dataset.label || custom || position || ""
  }

  #placementMissing() {
    return this.#zoneLabel() ? null : t("missing_zone")
  }

  #placementSummary() {
    const zone = this.#zoneLabel()
    if (!zone) return t("to_choose")

    const width = parseFloat(this.element.querySelector("[data-erp--marking-form-target='width']")?.value)
    const height = parseFloat(this.element.querySelector("[data-erp--marking-form-target='height']")?.value)
    if (!(width > 0 && height > 0)) return zone

    return `${zone} · ${t("dimensions", { width: MILLIMETRES.format(width), height: MILLIMETRES.format(height) })}`
  }

  // ── Step 3 · logo ────────────────────────────────────────────

  #fileCount() {
    const saved = this.element.querySelectorAll("[data-attachment-hidden-container] input[data-existing-attachment]").length
    return saved + this.fileInputTarget.files.length
  }

  #artworkMissing() {
    return this.#fileCount() > 0 || this.artworkPendingTarget.checked ? null : t("missing_artwork")
  }

  #artworkSummary() {
    const count = this.#fileCount()
    if (count > 0) return t("files", { count })
    return this.artworkPendingTarget.checked ? t("artwork_pending") : t("artwork_to_provide")
  }

  #setFiles(files) {
    this.fileInputTarget.files = this.#fileList(files)
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #fileList(files) {
    const transfer = new DataTransfer()
    files.forEach(file => transfer.items.add(file))
    return transfer.files
  }

  // The checks of erp--autosave (`accept`, `data-max-file-size`), so a dropped file is refused alone
  #fileError(file) {
    const accepted = this.fileInputTarget.accept.split(",").map(type => type.trim()).filter(Boolean)
    const maxSize = Number(this.fileInputTarget.dataset.maxFileSize)
    if (accepted.length > 0 && !accepted.includes(file.type)) return I18n.t("erp.sales.js.dropzone.unsupported_type")
    if (maxSize > 0 && file.size > maxSize) {
      return I18n.t("erp.sales.js.dropzone.file_too_large", { size: `${maxSize / (1024 * 1024)} Mo` })
    }
    return null
  }

  #renderPendingFiles() {
    const label = this.pendingFilesTarget.dataset.removeLabel || ""
    this.pendingFilesTarget.replaceChildren(...[ ...this.fileInputTarget.files ].map((file, index) => {
      const item = document.createElement("li")
      item.className = "flex items-center gap-3 rounded-xl border border-erp-line bg-erp-surface px-3 py-2.5 text-[13px]"
      const name = document.createElement("b")
      name.className = "min-w-0 flex-1 truncate"
      name.textContent = file.name
      const size = document.createElement("span")
      size.className = "text-erp-muted"
      size.textContent = this.#fileSize(file.size)
      item.append(name, size, this.#removeButton(label.replace("__NAME__", file.name), index))
      return item
    }))
  }

  #removeButton(label, index) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full border border-erp-line-strong bg-erp-surface hover:border-erp-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black"
    button.setAttribute("aria-label", label)
    button.dataset.action = "erp--marking-wizard#removePendingFile"
    button.setAttribute("data-erp--marking-wizard-index-param", index)
    button.innerHTML = '<i class="lni lni-xmark text-sm leading-none" aria-hidden="true"></i>'
    return button
  }

  #attachmentBadge(signedId, filename) {
    const badge = document.createElement("span")
    badge.className = "inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border border-erp-line-strong bg-erp-surface px-3 py-1 text-xs font-semibold"
    badge.dataset.attachmentBadge = "true"
    const name = document.createElement("span")
    name.className = "truncate"
    name.textContent = filename
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "shrink-0 cursor-pointer text-erp-danger"
    remove.dataset.action = "erp--autosave#removeAttachment"
    remove.dataset.signedId = signedId
    remove.setAttribute("aria-label", I18n.t("erp.sales.js.autosave.remove_file", { filename }))
    remove.textContent = "×"
    badge.append(name, remove)
    return badge
  }

  #fileSize(bytes) {
    return bytes > 1e6 ? t("size_mb", { size: (bytes / 1e6).toFixed(1).replace(".", ",") }) : t("size_kb", { size: Math.max(1, Math.round(bytes / 1e3)) })
  }

  // ── Step 4 · textiles ────────────────────────────────────────

  #allocationRows() {
    return [ ...this.element.querySelectorAll("[data-erp--marking-form-target='allocationRow']") ].filter(row => !row.classList.contains("hidden"))
  }

  #markedPieces() {
    return this.#allocationRows().reduce((sum, row) => {
      if (row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1") return sum
      return sum + (parseInt(row.querySelector("[data-erp--marking-form-target='allocationQuantity']")?.value) || 0)
    }, 0)
  }

  // A marking spread over saved sizes needs at least one piece; one without any (customer-supplied line without
  // sizes) keeps its own quantity, set in its pricing panel, and an older one its links to whole lines
  #textilesMissing() {
    if (this.#allocationRows().length === 0 || this.#markedPieces() > 0 || this.#linkedLines() > 0) return null
    return t("missing_textiles")
  }

  #linkedLines() {
    return [ ...this.element.querySelectorAll("[data-erp--marking-form-target='linkDestroy']") ].filter(field => field.value !== "1").length
  }

  #textilesSummary() {
    const pieces = this.#markedPieces()
    return pieces > 0 ? t("pieces", { count: pieces }) : t("to_choose")
  }
}
